import React, { useState, useRef, useEffect } from 'react';
import { Send, Paperclip, User, Shield, Loader2, Image as ImageIcon, X, Download } from 'lucide-react';
import api from '../../services/api';

const TicketConversation = ({ ticket, messages, onNewMessage, isAdmin = false }) => {
  const [newMessage, setNewMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await api.post('/tickets/upload-attachment', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setAttachments(prev => [...prev, {
          url: response.data.url,
          filename: response.data.filename
        }]);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Erreur lors de l\'upload du fichier');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if (!newMessage.trim() && attachments.length === 0) return;

    setSending(true);
    try {
      await api.post(`/tickets/${ticket.id}/messages`, {
        message: newMessage,
        attachments: attachments.map(a => a.url)
      });
      
      setNewMessage('');
      setAttachments([]);
      onNewMessage?.();
    } catch (error) {
      console.error('Send error:', error);
      alert('Erreur lors de l\'envoi du message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const renderAttachment = (url) => {
    const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const fullUrl = url.startsWith('http') ? url : `${backendUrl}${url}`;
    
    if (isImage) {
      return (
        <a href={fullUrl} target="_blank" rel="noopener noreferrer" className="block">
          <img 
            src={fullUrl} 
            alt="Pièce jointe" 
            className="max-w-xs rounded-lg border border-[#3A3A3D] hover:border-[#FF6B35] transition-colors"
          />
        </a>
      );
    }
    
    return (
      <a 
        href={fullUrl} 
        target="_blank" 
        rel="noopener noreferrer"
        className="flex items-center gap-2 px-3 py-2 bg-[#222226] rounded-lg hover:bg-[#2A2A2D] transition-colors"
      >
        <Download className="h-4 w-4 text-[#FF6B35]" />
        <span className="text-[#E8E8E8] text-sm">Télécharger le fichier</span>
      </a>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ maxHeight: '500px' }}>
        {messages.map((msg, index) => {
          const isAdminMessage = msg.sender_role === 'admin' || msg.sender_role === 'support';
          const isOwnMessage = isAdmin ? isAdminMessage : !isAdminMessage;
          
          return (
            <div 
              key={msg.id || index}
              className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[80%] ${isOwnMessage ? 'order-2' : 'order-1'}`}>
                {/* Sender info */}
                <div className={`flex items-center gap-2 mb-1 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                  {isAdminMessage ? (
                    <Shield className="h-4 w-4 text-[#FF6B35]" />
                  ) : (
                    <User className="h-4 w-4 text-[#00FFFF]" />
                  )}
                  <span className="text-xs text-[#6B6B6E]">
                    {msg.sender_name} • {formatDate(msg.created_at)}
                  </span>
                </div>
                
                {/* Message bubble */}
                <div 
                  className={`rounded-2xl px-4 py-3 ${
                    isAdminMessage 
                      ? 'bg-gradient-to-r from-[#FF6B35]/20 to-[#F7931E]/20 border border-[#FF6B35]/30' 
                      : 'bg-[#222226] border border-[#3A3A3D]'
                  }`}
                >
                  <p className="text-[#E8E8E8] whitespace-pre-wrap">{msg.message}</p>
                  
                  {/* Attachments */}
                  {msg.attachments && msg.attachments.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {msg.attachments.map((url, i) => (
                        <div key={i}>
                          {renderAttachment(url)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      {ticket.status !== 'closed' && (
        <div className="border-t border-[#2A2A2D] p-4">
          {/* Attachments preview */}
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {attachments.map((att, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-2 px-3 py-1.5 bg-[#222226] rounded-lg border border-[#3A3A3D]"
                >
                  <ImageIcon className="h-4 w-4 text-[#FF6B35]" />
                  <span className="text-xs text-[#E8E8E8] max-w-[150px] truncate">{att.filename}</span>
                  <button 
                    onClick={() => removeAttachment(index)}
                    className="text-[#6B6B6E] hover:text-[#FF3B5C]"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-3">
            {/* File input */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept="image/*,.pdf"
              className="hidden"
              multiple
            />
            
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="p-3 bg-[#222226] rounded-xl border border-[#3A3A3D] hover:border-[#FF6B35] transition-colors disabled:opacity-50"
              title="Ajouter une pièce jointe"
            >
              {uploading ? (
                <Loader2 className="h-5 w-5 animate-spin text-[#FF6B35]" />
              ) : (
                <Paperclip className="h-5 w-5 text-[#B8B8BA]" />
              )}
            </button>

            {/* Message input */}
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Écrivez votre message..."
              rows={1}
              className="flex-1 bg-[#222226] border border-[#3A3A3D] rounded-xl px-4 py-3 text-white placeholder-[#6B6B6E] focus:border-[#FF6B35] focus:outline-none resize-none"
            />

            {/* Send button */}
            <button
              onClick={handleSend}
              disabled={sending || (!newMessage.trim() && attachments.length === 0)}
              className="p-3 bg-gradient-to-r from-[#FF6B35] to-[#F7931E] rounded-xl hover:shadow-lg hover:shadow-[#FF6B35]/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? (
                <Loader2 className="h-5 w-5 animate-spin text-white" />
              ) : (
                <Send className="h-5 w-5 text-white" />
              )}
            </button>
          </div>
        </div>
      )}

      {ticket.status === 'closed' && (
        <div className="border-t border-[#2A2A2D] p-4 text-center">
          <p className="text-[#6B6B6E]">Ce ticket est fermé. Vous ne pouvez plus y répondre.</p>
        </div>
      )}
    </div>
  );
};

export default TicketConversation;
